import { IConfig } from "@kuika/kuika-cl-model";
import * as React from "react";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import { KuikaNameModal } from "../../ui-kit/kuika-name-modal/kuika-name-modal";
import { CONFIG_NAME_MAX_LENGTH, configNameRules } from "./config-manager-model";

/**
 * Asks for a configuration's name — a blank one to create, or a row's existing one to
 * rename. The sheet is `KuikaNameModal`; what lives here is what is actually
 * configuration-specific — the name rules — and `configNameRules` is the SAME rule set the
 * editor's own inline rename applies, so a name that can be created can always be re-saved,
 * and a name typed here is judged the same way either way.
 *
 * Both surfaces use it: the project's Config Manager and the workspace's Global
 * Configurations. `configs` is whichever list the new name must be unique within.
 */

export interface IConfigNameModalProps {
  visible: boolean;
  /** Heading copy, so "blank", "start from a global configuration" and "rename" each say what they do. */
  title: string;
  subtitle?: string;
  confirmLabel: string;
  isLoading: boolean;
  /**
   * Where the confirm's write stands, for the one mode that writes (rename): it rolls to its
   * loading label, then to its ✓ or "Try again". Replaces `isLoading` when given.
   */
  status?: KuikaButtonStatus;
  /** The list the uniqueness check runs against. */
  configs: IConfig[];
  /** Prefilled name — the global configuration's own when creating from one, or the row's own when renaming. */
  initialValue?: string;
  /** Rename mode: excludes the row being renamed from its own uniqueness check. */
  selfId?: string;
  /** Rename mode: an unchanged name stays saveable even if it now violates the rule. */
  originalName?: string;
  onCancel: () => void;
  onSubmit: (name: string) => void;
}

export class ConfigNameModal extends React.PureComponent<IConfigNameModalProps> {
  render() {
    return (
      <KuikaNameModal
        visible={this.props.visible}
        title={this.props.title}
        subtitle={this.props.subtitle}
        iconName="config-manager"
        iconType="topbar-icons"
        confirmLabel={this.props.confirmLabel}
        isLoading={this.props.isLoading}
        status={this.props.status}
        fieldName="configName"
        fieldLabel="Name"
        placeholder="Enter configuration name"
        initialValue={this.props.initialValue}
        maxLength={CONFIG_NAME_MAX_LENGTH}
        rules={configNameRules({
          configs: this.props.configs ?? [],
          selfId: this.props.selfId,
          originalName: this.props.originalName
        })}
        // The ids the four ConfigurationManager* scenarios and the Global Configs CRUD
        // suite have always typed into and clicked; they move with the field, not away —
        // rename mode reuses them rather than minting a second set for the same dialog shape.
        inputTestId="configNameInput"
        confirmTestId="createConfigButton"
        className="kuika-config-name-modal"
        onCancel={this.props.onCancel}
        onSubmit={this.props.onSubmit}
      />
    );
  }
}
